/**
 * AdminSocialCapitalLedger — last 50 point_events with user + reason
 * + amount so ops can debug awards without querying Mongo directly.
 * Admin-only. Route: /admin/social-capital
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Sparkles, RefreshCcw } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;
function token() {
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token") || "";
}

function fmtDT(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminSocialCapitalLedger() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${BASE}/admin/social-capital-ledger?limit=100`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      setEvents(d.events || []);
    } catch (e) { setError(e.message || "Load failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="admin-sc-ledger">
      <header className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Publisher · Admin</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "'Lora', serif" }}>
            Social Capital Ledger
          </h1>
          <p className="text-sm text-slate-600 mt-1">Last {events.length} point events across the Publisher graph. Newest first.</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          data-testid="admin-sc-ledger-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />} Refresh
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-sm mb-4">{error}</div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Reason</th>
              <th className="text-right px-4 py-3">Points</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && !loading && (
              <tr><td colSpan={4} className="text-center py-8 text-slate-400">No point events yet.</td></tr>
            )}
            {events.map((e, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-sc-ledger-row-${i}`}>
                <td className="px-4 py-2.5 text-xs text-slate-600">{fmtDT(e.created_at)}</td>
                <td className="px-4 py-2.5">
                  <div className="text-slate-900">{e.display_name || <span className="text-slate-400">anon</span>}</div>
                  {e.email && <div className="text-[10px] text-slate-500">{e.email}</div>}
                </td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-700">{e.reason}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-teal-700">
                  <span className="inline-flex items-center gap-0.5"><Sparkles className="w-3 h-3" />+{e.points}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
